// components/portal/TrialNotice.tsx

'use client';

import React, { useEffect, useId, useRef } from 'react';
import { X } from '@/components/ui/lucide-memo';
import styles from './Notice.module.css';

export const useNoticeDialog = () => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    dialog.showModal();
    headingRef.current?.focus();
    return () => {
      dialog.close();
      const target = opener?.isConnected ? opener : document.querySelector<HTMLElement>('[data-slides-welcome]') ?? document.querySelector<HTMLElement>('main button, button');
      target?.focus();
    };
  }, []);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    event.stopPropagation();
    if (event.key !== 'Tab') return;
    const controls = event.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])');
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  return { dialogRef, headingRef, headingId, onKeyDown };
};

export const TrialNotice = ({ label, onClose, children, actions, closeLabel = 'Got it' }: {
  label: string;
  closeLabel?: string;
  onClose: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) => {
  const { dialogRef, headingRef, headingId, onKeyDown } = useNoticeDialog();
  return (
    <dialog ref={dialogRef} aria-labelledby={headingId}
      onKeyDown={onKeyDown}
      onKeyUp={(event) => event.stopPropagation()}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      className={styles.dialog}>
      <div className={styles.header}>
        <h2 ref={headingRef} id={headingId} tabIndex={-1} className={styles.heading}>{label}</h2>
        <button type="button" onClick={onClose} className={styles.iconButton} aria-label="Close notice"><X size={18} /></button>
      </div>

      <div className={styles.body}>{children}</div>

      <div className={styles.footer}>
        {actions}
        <button
          type="button"
          onClick={onClose}
          className={styles.primary}
        >
          {closeLabel}
        </button>
      </div>
    </dialog>
  );
};

/** The one mail link every pilot notice points at. */
export const TrialFeedbackLink = ({ subject }: { subject: string }) => (
  <a
    href={`mailto:yg@fater.ai?subject=${encodeURIComponent(subject)}`}
    className="text-white underline underline-offset-2 hover:text-[#E8E8E8]"
  >
    yg@fater.ai
  </a>
);

/** Signed the same way in every tool. */
export const TrialNoticeSignature = () => (
  <p className={styles.signature}>Youssef Guessous - CEO</p>
);

export default TrialNotice;
